import { ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

type ExpandedProfileContentProps = {
  profileLoading: boolean;
  displayOverview: string | null;
  displaySpecialties: string[];
  profileImages: string[];
  activeImageIndex: number;
  isProfileImageLoading: boolean;
  name: string;
  providerId: string | undefined;
  onImageIndexChange: (index: number) => void;
  onImageLoad: (imageUrl: string) => void;
  onImageError: (imageUrl: string) => void;
};

export const ExpandedProfileContent = ({
  profileLoading,
  displayOverview,
  displaySpecialties,
  profileImages,
  activeImageIndex,
  isProfileImageLoading,
  name,
  providerId,
  onImageIndexChange,
  onImageLoad,
  onImageError,
}: ExpandedProfileContentProps) => {
  const navigate = useNavigate();

  const handleVisitProfile = () => {
    if (providerId) {
      navigate(`/providers/${providerId}`);
    }
  };
  if (profileLoading) {
    return (
      <div className="text-center py-8">
        <p className="text-sm font-termina text-[#4B5563]">Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,0.75fr)] gap-8 items-start ">
      <div className="space-y-6 border-r-2 border-[#E5E7EB] pr-6">
        {displayOverview && (
          <section>
            <div className="flex items-center gap-3 mb-3">
              <h6 className="text-xl font-recoleta font-semibold text-[#111827]">Overview</h6>
              {providerId && (
                <button
                  type="button"
                  onClick={handleVisitProfile}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-termina text-white bg-[#026857] hover:bg-[#025246] rounded-md transition-colors"
                >
                  <span>Visit Profile</span>
                  <ExternalLink className="w-4 h-4" />
                </button>
              )}
            </div>
            <p className="text-sm font-termina text-[#4B5563] leading-relaxed whitespace-pre-line">
              {displayOverview}
            </p>
          </section>
        )}
        {displaySpecialties.length > 0 && (
          <section>
            <h6 className="text-xl font-recoleta font-semibold text-[#111827] mb-3">
              Specialties
            </h6>
            <ul className="space-y-1.5">
              {displaySpecialties.map((item: string, index: number) => (
                <li key={index} className="flex items-start gap-2.5 text-sm font-termina text-[#111827]">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#026857]" />
                  <span className="leading-relaxed flex-1">{item}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
      <div className="flex flex-col gap-4 items-center">
        <div className="relative w-[210px] max-w-full md:w-[220px]">
          <div className="overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white relative">
            {isProfileImageLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#E5E7EB] border-t-[#026857]"></div>
              </div>
            )}
            {profileImages.length > 0 ? (
              <>
                <img
                  key={activeImageIndex}
                  src={profileImages[activeImageIndex]}
                  alt={`${name} - Image ${activeImageIndex + 1}`}
                  className={`w-full aspect-square object-contain bg-gray-50 transition-opacity duration-300 ${isProfileImageLoading ? 'opacity-0' : 'opacity-100'}`}
                  loading="lazy"
                  onLoad={() => onImageLoad(profileImages[activeImageIndex])}
                  onError={() => onImageError(profileImages[activeImageIndex])}
                />
                {profileImages.length > 1 && (
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                    {profileImages.map((_, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => onImageIndexChange(index)}
                        className={`h-2 rounded-full transition-all ${
                          index === activeImageIndex
                            ? 'w-6 bg-[#026857]'
                            : 'w-2 bg-white/60 hover:bg-white/80'
                        }`}
                        aria-label={`Go to image ${index + 1}`}
                      />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="w-full aspect-square bg-[#F3F4F6] flex items-center justify-center">
                {isProfileImageLoading ? (
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#E5E7EB] border-t-[#026857]"></div>
                ) : (
                  <span className="text-sm font-termina text-[#6B7280]">No image available</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

